"use client";

import {
	citationMix,
	coverageFunnel,
	hiddenMediaItems,
	mcpConversation,
} from "@marketing/shared/lib/bento-fixtures";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { useTranslations } from "next-intl";
import { useRevealed } from "./motion";

/**
 * Shared data tiles for the B2 pages. Sample data stays EN (screenshot
 * convention); chrome reads from the owning page's widgets namespace.
 */

/** Generic ranked bar rows (markets, products, publishers). */
export function RankedBarList({
	rows,
	suffix = "%",
	className,
}: {
	rows: ReadonlyArray<{ label: string; sublabel: string; value: number }>;
	suffix?: string;
	className?: string;
}) {
	const revealed = useRevealed();
	const max = Math.max(...rows.map((r) => r.value), 1);
	return (
		<ul className={cn("space-y-3", className)}>
			{rows.map((row, i) => (
				<li key={row.label}>
					<div className="flex items-baseline justify-between gap-2">
						<span className="flex min-w-0 items-baseline gap-2">
							<span className="truncate text-sm font-medium">
								{row.label}
							</span>
							<span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
								{row.sublabel}
							</span>
						</span>
						<span className="shrink-0 font-mono text-sm font-semibold tabular-nums">
							{row.value.toFixed(suffix === "%" ? 1 : 0)}
							{suffix}
						</span>
					</div>
					<div className="mt-1 h-1.5 rounded-full bg-muted">
						<div
							className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
							style={{
								width: revealed
									? `${(row.value / max) * 100}%`
									: "0%",
								backgroundColor: CHART_SERIES[0],
								transitionDelay: `${i * 120}ms`,
							}}
						/>
					</div>
				</li>
			))}
		</ul>
	);
}

/** Stacked citation-classification mix per week. Direct legend, no axes. */
export function CitationMixBars({ className }: { className?: string }) {
	const t = useTranslations("marketing.citationIntelligence.widgets");
	const revealed = useRevealed();
	const segments = [
		{ key: "attributed" as const, color: CHART_SERIES[0], opacity: 1 },
		{ key: "contentMention" as const, color: CHART_SERIES[1], opacity: 1 },
		{
			key: "unrelated" as const,
			color: "var(--muted-foreground)",
			opacity: 0.35,
		},
	];
	return (
		<div className={cn("space-y-3", className)}>
			{citationMix.map((week) => (
				<div key={week.week}>
					<div className="flex items-baseline justify-between text-xs">
						<span className="font-mono text-muted-foreground">
							{week.week}
						</span>
						<span className="font-mono tabular-nums text-muted-foreground">
							{t("attributedShare", { pct: week.attributed })}
						</span>
					</div>
					<div className="mt-1 flex h-2.5 overflow-hidden rounded-full bg-muted">
						{segments.map((segment) => (
							<div
								key={segment.key}
								className="h-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
								style={{
									width: revealed
										? `${week[segment.key]}%`
										: "0%",
									backgroundColor: segment.color,
									opacity: segment.opacity,
								}}
							/>
						))}
					</div>
				</div>
			))}
			<div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[11px] text-muted-foreground">
				{segments.map((segment) => (
					<span
						key={segment.key}
						className="flex items-center gap-1.5"
					>
						<span
							className="size-2 rounded-full"
							style={{
								backgroundColor: segment.color,
								opacity: segment.opacity,
							}}
						/>
						{t(`legend.${segment.key}`)}
					</span>
				))}
			</div>
		</div>
	);
}

/** Hidden-earned-media example cards. */
export function HiddenMediaCards({ className }: { className?: string }) {
	const t = useTranslations("marketing.citationIntelligence.widgets");
	return (
		<div className={cn("space-y-3", className)}>
			{hiddenMediaItems.map((item) => (
				<div
					key={item.page}
					className="rounded-xl border bg-background p-4"
				>
					<div className="flex items-center gap-2">
						<Badge
							status={
								item.kind === "hidden" ? "warning" : "error"
							}
						>
							{item.kind === "hidden"
								? t("badgeHidden")
								: t("badgeCompetitor")}
						</Badge>
					</div>
					<p className="mt-2 truncate font-mono text-sm text-foreground/90">
						{item.page}
					</p>
					<p className="mt-1.5 text-xs text-muted-foreground">
						{item.note}
					</p>
				</div>
			))}
		</div>
	);
}

/**
 * MCP conversation tile styled after the real Claude chat UI: conversation
 * title up top, user messages as right-aligned bubbles, assistant replies as
 * plain text with a thinking line and source chips, and the composer bar at
 * the bottom. The Claude logo (same public asset the product uses) anchors
 * the brand; colors stay on theme tokens so both modes hold up.
 */
export function McpTerminalTile({ className }: { className?: string }) {
	const revealed = useRevealed();
	const messageClass = cn(
		"motion-safe:transition-all motion-safe:duration-500",
		revealed
			? "motion-safe:translate-y-0 motion-safe:opacity-100"
			: "motion-safe:translate-y-2 motion-safe:opacity-0",
	);
	const messageStyle = (i: number) => ({ transitionDelay: `${i * 260}ms` });

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			{/* Conversation header */}
			<div className="flex items-center justify-between">
				<span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
					Publisher citations review
					<svg
						viewBox="0 0 8 5"
						className="size-2 fill-none stroke-current"
						aria-hidden
					>
						<path d="M1 1l3 3 3-3" strokeWidth="1.2" />
					</svg>
				</span>
				<span className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-bold text-muted-foreground">
					Spyglasses MCP · connected
				</span>
			</div>

			{mcpConversation.map((message, i) =>
				message.role === "user" ? (
					<div
						key={message.text}
						className={cn(
							"ml-auto max-w-[85%] rounded-2xl bg-muted px-3.5 py-2 text-xs leading-relaxed text-foreground",
							messageClass,
						)}
						style={messageStyle(i)}
					>
						{message.text}
					</div>
				) : (
					<div
						key={message.text}
						className={cn("max-w-[92%]", messageClass)}
						style={messageStyle(i)}
					>
						{i === 1 ? (
							<p className="mb-1 text-[11px] italic text-muted-foreground/70">
								Queried the Spyglasses MCP server
							</p>
						) : null}
						<p className="text-xs leading-relaxed text-foreground/90">
							{message.text}{" "}
							<span className="inline-flex translate-y-px items-center rounded-full border bg-muted px-1.5 py-px text-[9px] font-medium text-muted-foreground">
								Spyglasses
							</span>
						</p>
					</div>
				),
			)}

			{/* Composer bar */}
			<div className="mt-1 flex items-center gap-2 rounded-2xl border bg-background px-3 py-2.5">
				<span
					className="flex size-5 shrink-0 items-center justify-center rounded-full border text-muted-foreground"
					aria-hidden
				>
					<svg
						viewBox="0 0 10 10"
						className="size-2.5 fill-none stroke-current"
					>
						<path d="M5 1v8M1 5h8" strokeWidth="1.2" />
					</svg>
				</span>
				<span className="flex-1 text-xs text-muted-foreground">
					Write a message…
				</span>
				<span className="flex shrink-0 items-center gap-1.5">
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img
						src="/images/claude-logo.svg"
						alt=""
						className="size-4"
						aria-hidden
					/>
					<span className="text-xs font-semibold text-muted-foreground">
						Claude
					</span>
				</span>
			</div>
		</div>
	);
}

/** Three-stage coverage funnel: placements → cited by AI → cited inline. */
export function CoverageFunnelTile({ className }: { className?: string }) {
	const t = useTranslations("marketing.forPRFirms.v2.widgets");
	const revealed = useRevealed();
	const stages = [
		{
			key: "placements",
			label: t("stagePlacements"),
			value: coverageFunnel.placements,
			color: "var(--muted-foreground)",
			opacity: 0.35,
		},
		{
			key: "cited",
			label: t("stageCited"),
			value: coverageFunnel.cited,
			color: CHART_SERIES[0],
			opacity: 0.55,
		},
		{
			key: "citedInline",
			label: t("stageInline"),
			value: coverageFunnel.citedInline,
			color: CHART_SERIES[0],
			opacity: 1,
		},
	];
	const max = Math.max(...stages.map((s) => s.value), 1);
	return (
		<div className={cn("space-y-3", className)}>
			{stages.map((stage, i) => (
				<div key={stage.key}>
					<div className="flex items-baseline justify-between text-xs">
						<span className="font-medium">{stage.label}</span>
						<span className="font-mono text-sm font-semibold tabular-nums">
							{stage.value}
						</span>
					</div>
					<div className="mt-1 h-2.5 rounded-full bg-muted">
						<div
							className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
							style={{
								width: revealed
									? `${(stage.value / max) * 100}%`
									: "0%",
								backgroundColor: stage.color,
								opacity: stage.opacity,
								transitionDelay: `${i * 160}ms`,
							}}
						/>
					</div>
				</div>
			))}
			<p className="pt-0.5 text-[11px] text-muted-foreground">
				{t("funnelCaption")}
			</p>
		</div>
	);
}
